import { SharingFieldOrderItem } from "./Sharings.types";

// Mirror of Backend/Core/Filtering/Sharings/SharingColumn.cs. Values are explicit and must stay in step: they travel
// in the search request, in sortByColumn and in the saved view blobs the backend serves.
export enum SharingColumn {
    Name = 0,
    ProcessingActivity = 1,
    System = 2,
    SharingType = 3,
    Description = 4,
    Sender = 5,
    SenderRole = 6,
    SenderCountry = 7,
    Recipient = 8,
    RecipientRole = 9,
    RecipientCountry = 10,
    Responsible = 11,
    DataCategories = 12,
    TransferBasis = 13,
    TransferImpactAssessment = 14,
    ReceivingLocation = 15,
    SenderAddress = 16,
    RecipientAddress = 17,
    DataProcessorAgreement = 18,
    Agreement = 19,
    StorageLocations = 20,
    SupportAccessLocations = 21,
    DisclosureLegalBases = 22,
    DisclosureDoubleLegalBases = 23,
    DisclosureNationalLaws = 24,
    GroupCompanies = 25,
    DocumentTypes = 26,
    DocumentCount = 27,
    NoteToSubProcessor = 28,

    // Read off the sharing's processing activity, the way the custom list repeated them on every sharing.
    Purposes = 29,
    DataSubjects = 30,
    SecurityMeasures = 31,
    NumberOfDataSubjects = 32,
    NumberOfProcessors = 33,
    CustomNumberOfDataSubjects = 34,
    CustomNumberOfProcessors = 35,
    BusinessAreas = 36,
    DeletionPeriods = 37,
    Tags = 38,
}

// The string ids the column picker, the table headers and user-saved views work in. A sharing has no template, so
// the vocabulary is known up front.
export const SharingColumnId = {
    name: "name",
    processingActivity: "processingActivity",
    system: "system",
    sharingType: "sharingType",
    description: "description",
    sender: "sender",
    senderRole: "senderRole",
    senderCountry: "senderCountry",
    recipient: "recipient",
    recipientRole: "recipientRole",
    recipientCountry: "recipientCountry",
    responsible: "responsible",
    dataCategories: "dataCategories",
    transferBasis: "transferBasis",
    transferImpactAssessment: "transferImpactAssessment",
    receivingLocation: "receivingLocation",
    senderAddress: "senderAddress",
    recipientAddress: "recipientAddress",
    dataProcessorAgreement: "dataProcessorAgreement",
    agreement: "agreement",
    storageLocations: "storageLocations",
    supportAccessLocations: "supportAccessLocations",
    disclosureLegalBases: "disclosureLegalBases",
    disclosureDoubleLegalBases: "disclosureDoubleLegalBases",
    disclosureNationalLaws: "disclosureNationalLaws",
    groupCompanies: "groupCompanies",
    documentTypes: "documentTypes",
    documentCount: "documentCount",
    noteToSubProcessor: "noteToSubProcessor",
    purposes: "purposes",
    dataSubjects: "dataSubjects",
    securityMeasures: "securityMeasures",
    numberOfDataSubjects: "numberOfDataSubjects",
    numberOfProcessors: "numberOfProcessors",
    customNumberOfDataSubjects: "customNumberOfDataSubjects",
    customNumberOfProcessors: "customNumberOfProcessors",
    businessAreas: "businessAreas",
    deletionPeriods: "deletionPeriods",
    tags: "tags",
} as const;

// Derived from the ids above rather than written out a second time, so the two cannot fall out of step: each id
// is its enum member's name with a lower-case first letter.
export const sharingColumnByFilterId: Record<string, SharingColumn> = Object.fromEntries(
    Object.entries(SharingColumnId).map(([member, filterId]) => [
        filterId,
        SharingColumn[(member.charAt(0).toUpperCase() + member.slice(1)) as keyof typeof SharingColumn],
    ])
);

const sharingFilterIdByColumn: Record<number, string> = Object.fromEntries(
    Object.entries(sharingColumnByFilterId).map(([filterId, column]) => [column, filterId])
);
export function getSharingFilterIdByColumn(column: SharingColumn): string | undefined {
    return sharingFilterIdByColumn[column];
}

// Order is the order the user arranged them in. An id the vocabulary no longer has is dropped rather than sent.
export function toSharingDisplayFields(filterIds: Array<string>): Array<SharingFieldOrderItem> {
    return filterIds
        .map((filterId, index) => ({ column: sharingColumnByFilterId[filterId], displayOrder: index }))
        .filter((field) => field.column !== undefined);
}
